import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";
import { isSupabaseConnected, fetchWorkshops, fetchWorkshopSessionsWithCounts, type WorkshopSessionWithCount } from "./supabase";
import type { AdminWorkshop } from "./admin/AdminDataContext";

function mapRowToWorkshop(row: {
  id: number | string;
  title: string;
  date: string;
  location: string;
  image?: string | null;
  description?: string | null;
  i18n?: Record<string, unknown> | null;
}): AdminWorkshop {
  return {
    id: Number(row.id),
    title: row.title,
    date: row.date,
    location: row.location,
    image: row.image ?? "",
    description: row.description ?? undefined,
    i18n: (row.i18n ?? {}) as AdminWorkshop["i18n"],
  };
}

export function WorkshopSection() {
  const { workshops: contextWorkshops } = useAdminData();
  const { lang, t } = useLanguage();
  const theme = useTheme();
  const [sessions, setSessions] = useState<WorkshopSessionWithCount[]>([]);
  const [workshops, setWorkshops] = useState<AdminWorkshop[]>(() =>
    isSupabaseConnected() ? [] : contextWorkshops
  );
  const [useSessions, setUseSessions] = useState(false);

  useEffect(() => {
    if (!isSupabaseConnected()) {
      setWorkshops(contextWorkshops);
      return;
    }
    fetchWorkshopSessionsWithCounts({ upcomingOnly: true }).then(({ data, error }) => {
      if (!error && data && data.length > 0) {
        setSessions(data);
        setUseSessions(true);
        return;
      }
      fetchWorkshops().then(({ data: wData, error: wErr }) => {
        if (wErr) {
          setWorkshops(contextWorkshops);
          return;
        }
        setWorkshops((wData ?? []).map(mapRowToWorkshop));
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch once on mount

  useEffect(() => {
    if (isSupabaseConnected()) return;
    setWorkshops(contextWorkshops);
  }, [contextWorkshops]);

  if (useSessions && sessions.length > 0) {
    return (
      <section id="workshops" className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="tracking-[0.3em] uppercase mb-3" style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}>
            {t("workshops.label")}
          </p>
          <h2 className="tracking-[0.1em]" style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}>
            {t("workshops.title")}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {sessions.map((session) => {
            const title = session.title_override || session.template_title;
            const location = session.location_override || session.template_location;
            const maxCap = session.max_capacity ?? 0;
            const count = Number(session.subscriber_count ?? 0);
            const isFull = maxCap > 0 && count >= maxCap;
            return (
              <div
                key={session.id}
                className="group bg-white rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(40,32,20,0.06)] hover:shadow-[0_12px_40px_rgba(40,32,20,0.1)] transition-all duration-500 hover:-translate-y-0.5"
              >
                <div className="overflow-hidden">
                  {session.template_image_url ? (
                    <ImageWithFallback
                      src={session.template_image_url}
                      alt={title ?? ""}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-[#F7F5F1]" />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="tracking-[0.05em] mb-3" style={{ fontFamily: theme.fontHeading, fontSize: "22px", fontWeight: 500, color: theme.fg }}>
                    {title}
                  </h3>
                  <p className="tracking-[0.1em] mb-1" style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 400, color: theme.accent }}>
                    {new Date(session.starts_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
                  </p>
                  {location && (
                    <p className="tracking-[0.05em] mb-4" style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.fg }}>
                      {location}
                    </p>
                  )}
                  {maxCap > 0 && (
                    <p className="text-sm mb-4" style={{ color: theme.muted }}>
                      {count} / {maxCap} spots
                    </p>
                  )}
                  <Link
                    to={`/workshops/${session.id}/reserve`}
                    className="block w-full py-3 border text-center tracking-[0.2em] uppercase transition-all duration-300"
                    style={{
                      fontFamily: theme.fontBody,
                      fontSize: "11px",
                      fontWeight: 400,
                      borderColor: theme.accent,
                      color: theme.accent,
                      borderRadius: theme.btnRadius,
                    }}
                  >
                    {isFull ? (t("workshops.join_waitlist") ?? "Join waitlist") : t("workshops.reserve")}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  if (workshops.length === 0) return null;

  return (
    <section id="workshops" className="py-24 px-6 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="tracking-[0.3em] uppercase mb-3" style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}>
          {t("workshops.label")}
        </p>
        <h2 className="tracking-[0.1em]" style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}>
          {t("workshops.title")}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {workshops.map((workshop) => (
          <div
            key={workshop.id}
            className="group bg-white rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(40,32,20,0.06)] hover:shadow-[0_12px_40px_rgba(40,32,20,0.1)] transition-all duration-500 hover:-translate-y-0.5"
          >
            <div className="overflow-hidden">
              <ImageWithFallback
                src={workshop.image}
                alt={workshop.title}
                className="w-full aspect-[4/3] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
              />
            </div>
            <div className="p-6">
              <h3 className="tracking-[0.05em] mb-3" style={{ fontFamily: theme.fontHeading, fontSize: "22px", fontWeight: 500, color: theme.fg }}>
                {localized(workshop, "title", lang)}
              </h3>
              <p className="tracking-[0.1em] mb-1" style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 400, color: theme.accent }}>
                {workshop.date}
              </p>
              <p className="tracking-[0.05em] mb-6" style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.fg }}>
                {localized(workshop, "location", lang)}
              </p>
              <span
                className="block w-full py-3 border text-center tracking-[0.2em] uppercase opacity-60"
                style={{ fontFamily: theme.fontBody, fontSize: "11px", borderColor: theme.accent, color: theme.accent, borderRadius: theme.btnRadius }}
              >
                {t("workshops.reserve")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}